import { Globe, Video, Sparkles, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import studioCardImage from 'figma:asset/a36a7f20942867ad3a02065df136b6956401c8a5.png';

interface AdStudioLandingPageProps {
  onNavigate: (page: string) => void;
}

export function AdStudioLandingPage({ onNavigate }: AdStudioLandingPageProps) {
  const studioOptions = [
    {
      id: 'ugc-dub',
      title: 'UGC-Dub Localization',
      subtitle: 'Global in 40+ languages',
      description: 'AI-powered voice dubbing and localization for your UGC content',
      image: studioCardImage,
      icon: Globe,
      gradient: 'from-green-600 to-emerald-700',
      status: 'beta',
      onClick: () => onNavigate('audio-dubbing'),
    },
    {
      id: 'video-studio',
      title: 'Video Ad Studio',
      subtitle: 'Professional editing tools',
      description: 'Create and edit video ads with timeline-based editing',
      image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4',
      icon: Video,
      gradient: 'from-purple-600 to-blue-700',
      status: 'active',
      onClick: () => onNavigate('video-ad-studio'),
    },
    {
      id: 'ai-generator',
      title: 'AI Creative Generator',
      subtitle: 'Generate ads from text',
      description: 'Create video ads using AI from text descriptions',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995',
      icon: Sparkles,
      gradient: 'from-pink-600 to-purple-700',
      status: 'coming-soon',
      onClick: () => {},
    },
    {
      id: 'batch-editor',
      title: 'Batch Ad Creator',
      subtitle: 'Scale your production',
      description: 'Create multiple ad variations simultaneously',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
      icon: Wand2,
      gradient: 'from-blue-600 to-cyan-700',
      status: 'coming-soon',
      onClick: () => {},
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#101828]">Ad Studio</h1>
              <p className="text-gray-500 mt-1">Create, edit, and localize your advertising content</p>
            </div>
          </div>
        </div>
      </div>

      {/* Studio Options Grid */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {studioOptions.map((studio) => {
            const IconComponent = studio.icon;
            
            return (
              <Card
                key={studio.id}
                className="group relative overflow-hidden cursor-pointer transition-all hover:shadow-xl border-0 bg-white"
                onClick={studio.status !== 'coming-soon' ? studio.onClick : undefined}
              >
                {/* Background Image */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={studio.image}
                    alt={studio.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-b ${studio.gradient} opacity-60 mix-blend-multiply`} />
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Status Badge */}
                  {studio.status === 'beta' && (
                    <div className="absolute top-4 right-4">
                      <div className="px-3 py-1 rounded-full bg-orange-500 text-white text-xs font-medium">
                        Beta
                      </div>
                    </div>
                  )}
                  {studio.status === 'active' && (
                    <div className="absolute top-4 right-4">
                      <div className="px-3 py-1 rounded-full bg-green-500 text-white text-xs font-medium">
                        Active
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="w-12 h-1 bg-white/40 rounded-full mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">{studio.title}</h3>
                  <p className="text-white/90 text-sm mb-4">{studio.subtitle}</p>
                  
                  {/* CTA Button */}
                  {studio.status === 'coming-soon' ? (
                    <div>
                      <Button
                        variant="secondary"
                        className="w-full bg-white/10 hover:bg-white/20 text-white border-white/20 backdrop-blur-sm mb-2"
                        disabled
                      >
                        Coming Soon
                      </Button>
                      <button className="w-full text-white/80 hover:text-white text-sm transition-colors">
                        Apply for beta
                      </button>
                    </div>
                  ) : studio.status === 'beta' ? (
                    <div>
                      <Button
                        className="w-full bg-white hover:bg-gray-100 text-gray-900 mb-2"
                        onClick={studio.onClick}
                      >
                        Try Beta
                      </Button>
                      <button className="w-full text-white/80 hover:text-white text-sm transition-colors">
                        Request access
                      </button>
                    </div>
                  ) : (
                    <Button
                      className="w-full bg-white hover:bg-gray-100 text-gray-900"
                      onClick={studio.onClick}
                    >
                      Open Studio
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-xl border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">What's Ad Studio?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-3">
                <Wand2 className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">AI-Powered Tools</h3>
              <p className="text-sm text-gray-600">
                Leverage cutting-edge AI to create, edit, and localize your content faster than ever
              </p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-3">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Global Reach</h3>
              <p className="text-sm text-gray-600">
                Automatically localize your ads to 40+ languages with voice cloning and lip-sync
              </p>
            </div>
            <div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mb-3">
                <Video className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Professional Quality</h3>
              <p className="text-sm text-gray-600">
                Create broadcast-quality ads with timeline editing, effects, and advanced controls
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
